// The reader's middlegame by kind of position (`GameFacts.middlegamePositions`):
// each of the reader's middlegame moves carries the score change since their
// previous move (the opponent's move and their own, from the reader's side,
// capped), and that change goes to every kind of the position the move was
// played in (`positionTypesOf`, read from the position before the move). So
// the changes of all the reader's moves add up to the middlegame's swing
// (`stageSwings.middlegame`). A kind counts for the game only when the
// reader made at least `MIDDLEGAME_POSITION_MIN_MOVES` moves in it.
import { MIDDLEGAME_POSITION_MIN_MOVES, type GameFacts, type MiddlegamePosition, type PositionShare } from '@chess/api-model';
import type { FactExtractor, ScannedGame } from '../extractor.ts';
import { positionTypesOf } from '../position-types.ts';

export const middlegamePositions: FactExtractor = {
  id: 'middlegame-positions',
  extract(game: ScannedGame, facts: GameFacts): void {
    const { fromPly, toPly } = game.range('middlegame');
    if (toPly <= fromPly) return;
    const shares: Partial<Record<MiddlegamePosition, PositionShare>> = {};
    // The range starts and ends on the reader's moves, so the changes tile it.
    for (let ply = fromPly + 1; ply <= toPly; ply++) {
      if (!game.isReaders(ply)) continue;
      const fen = game.at(ply - 1)?.fen;
      const before = game.pawnsAt(Math.max(fromPly, ply - 2));
      const after = game.pawnsAt(ply);
      if (!fen || before === undefined || after === undefined) continue;
      for (const kind of positionTypesOf(fen, game.reader)) {
        const share = (shares[kind] ??= { moves: 0, change: 0 });
        share.moves += 1;
        share.change += after - before;
      }
    }
    const out: Partial<Record<MiddlegamePosition, PositionShare>> = {};
    for (const [kind, share] of Object.entries(shares) as Array<[MiddlegamePosition, PositionShare]>)
      if (share.moves >= MIDDLEGAME_POSITION_MIN_MOVES) out[kind] = share;
    facts.middlegamePositions = out;
  },
};
